import React from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Read-only modal that summarises the outcome of a bulk-parse run: total /
 * parsed / errors counts plus per-document detail rows.
 */
const BulkResultsDialog = ({
  open,
  onOpenChange,
  bulkResults,
  documents,
  onViewDoc,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border border-zinc-200 rounded-none p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Bulk Parse Results</DialogTitle>
        </DialogHeader>
        {bulkResults && (
          <div className="p-6 pt-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-zinc-50 border border-zinc-200 p-3 text-center">
                <p className="font-mono text-2xl font-bold">{bulkResults.summary.total}</p>
                <p className="text-[10px] font-heading uppercase tracking-wider text-zinc-400">Total</p>
              </div>
              <div className="bg-green-50 border border-green-200 p-3 text-center">
                <p className="font-mono text-2xl font-bold text-green-700">{bulkResults.summary.parsed}</p>
                <p className="text-[10px] font-heading uppercase tracking-wider text-green-600">Parsed</p>
              </div>
              <div className={`${bulkResults.summary.errors > 0 ? 'bg-red-50 border-red-200' : 'bg-zinc-50 border-zinc-200'} border p-3 text-center`}>
                <p className={`font-mono text-2xl font-bold ${bulkResults.summary.errors > 0 ? 'text-red-700' : 'text-zinc-400'}`}>{bulkResults.summary.errors}</p>
                <p className={`text-[10px] font-heading uppercase tracking-wider ${bulkResults.summary.errors > 0 ? 'text-red-600' : 'text-zinc-400'}`}>Errors</p>
              </div>
            </div>

            <div className="border border-zinc-200 max-h-64 overflow-y-auto">
              {bulkResults.results.map((r, i) => {
                const doc = documents.find(d => d.document_id === r.document_id);
                return (
                  <div
                    key={r.document_id}
                    data-testid={`bulk-result-${i}`}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-100 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}
                  >
                    {r.status === 'parsed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc?.title || r.document_id}</p>
                      {r.status === 'parsed' ? (
                        <p className="text-xs text-zinc-500">
                          {r.vendor_name || 'Unknown vendor'}
                          {r.line_items_count > 0 && ` | ${r.line_items_count} items`}
                          {r.total && ` | $${r.total.toFixed(2)}`}
                        </p>
                      ) : (
                        <p className="text-xs text-red-500">{r.error}</p>
                      )}
                    </div>
                    {r.status === 'parsed' && (
                      <button
                        onClick={() => onViewDoc(r.document_id)}
                        className="text-[10px] font-heading font-semibold uppercase tracking-wider text-primary hover:underline flex-shrink-0"
                      >
                        View
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => onOpenChange(false)}
              className="w-full h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm border border-zinc-200 hover:bg-primary-hover"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkResultsDialog;
